import { lazyWithPreload as lazy, type PreloadableComponent } from 'react-lazy-with-preload'
import type { FeatureName } from 'src/features'

type tRoutes = Record<string, {
  Component: PreloadableComponent<React.FC<{}>>
  paths: string[]
  right?: string
  feature?: FeatureName
}>

const routes = {

  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  Start: {
    Component: lazy(()=>import('src/routes/Start/Start')),
    paths: ['/'],
  },
  Login: {
    Component: lazy(()=>import('src/routes/Login/Login')),
    paths: ['/login'],
  },
  Hilfe: {
    Component: lazy(()=>import('src/routes/Hilfe/Hilfe')),
    paths: ['/hilfe'],
  },

  
  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /* ++++++++++++++++++++++++ Produkte +++++++++++++++++++++++++++++ */
  Produktfilter: {
    Component: lazy(()=>import('src/routes/Produktfilter/Produktfilter')),
    paths: ['/produktfilter'],
    right: 'produkte.filter',
  },
  ProduktSeoKontrolle: {
    Component: lazy(()=>import('src/routes/ProduktSeoKontrolle/ProduktSeoKontrolle')),
    paths: ['/produkt-seo-kontrolle'],
    right: 'produkte.filter',
  },
  Produkt: {
    Component: lazy(()=>import('src/routes/Produkt/Produkt')),
    paths: ['/produkt/','/produkt/:id'],
    right: 'produkte.anzeigen',
  },
  NeuesProdukt: {
    Component: lazy(()=>import('src/routes/NeuesProdukt/NeuesProdukt')),
    paths: ['/neuesprodukt'],
    right: 'produkte.bearbeiten',
  },
  ProduktAIFilter: {
    Component: lazy(()=>import('src/routes/ProduktAIFilter/ProduktAIFilter')),
    paths: ['/produktaifilter'],
    right: 'produkte.importe',
    feature: 'productImports',
  },
  SocialImages: {
    Component: lazy(()=>import('src/routes/SocialImages/SocialImages')),
    paths: ['/social-images'],
    right: 'produkte.bearbeiten',
    feature: 'socialImages',
  },
  EbayVorlagenfilter: {
    Component: lazy(()=>import('src/routes/EbayVorlagenfilter/EbayVorlagenfilter')),
    paths: ['/ebayvorlagen'],
    right: 'produkte.filter',
  },
  EbayVorlage: {
    Component: lazy(()=>import('src/routes/EbayVorlage/EbayVorlage')),
    paths: ['/ebayvorlage/','/ebayvorlage/:id'],
    right: 'produkte.anzeigen',
  },
  IdealoEinstellungen: {
    Component: lazy(()=>import('src/routes/IdealoEinstellungen/IdealoEinstellungen')),
    paths: ['/idealo-einstellungen'],
    right: 'idealo.anzeigen',
  },
  NeueEbayVorlage: {
    Component: lazy(()=>import('src/routes/NeueEbayVorlage/NeueEbayVorlage')),
    paths: ['/neueebayvorlage'],
    right: 'produkte.bearbeiten',
  },
  NeuerProduktAI: {
    Component: lazy(()=>import('src/routes/NeuerProduktAI/NeuerProduktAI')),
    paths: ['/neuerproduktai'],
    right: 'produkte.importe',
    feature: 'productImports',
  },
  ProduktAI: {
    Component: lazy(()=>import('src/routes/ProduktAI/ProduktAI')),
    paths: ['/produktai/','/produktai/:id'],
    right: 'produkte.importe',
    feature: 'productImports',
  },
  Rabattefilter: {
    Component: lazy(()=>import('src/routes/Rabattefilter/Rabattefilter')),
    paths: ['/rabattefilter'],
    right: 'discounts.filter',
  },
  Rabatt: {
    Component: lazy(()=>import('src/routes/Rabatt/Rabatt')),
    paths: ['/rabatt/','/rabatt/:id'],
    right: 'discounts.anzeigen',
  },
  NeuerRabatt: {
    Component: lazy(()=>import('src/routes/NeuerRabatt/NeuerRabatt')),
    paths: ['/neuerrabatt'],
    right: 'discounts.bearbeiten',
  },
  IdealoAuswertung: {
    Component: lazy(()=>import('src/routes/IdealoAuswertung/IdealoAuswertung')),
    paths: ['/idealo-auswertung'],
    right: 'idealo.anzeigen',
  },
  IdealoArbeitsliste: {
    Component: lazy(()=>import('src/routes/IdealoArbeitsliste/IdealoArbeitsliste')),
    paths: ['/idealo-arbeitsliste'],
    right: 'idealo.anzeigen',
  },
  IdealoTrafficAuswertung: {
    Component: lazy(()=>import('src/routes/IdealoTrafficAuswertung/IdealoTrafficAuswertung')),
    paths: ['/idealo-traffic'],
    right: 'idealo.anzeigen',
  },
  UmsatzAuswertung: {
    Component: lazy(()=>import('src/routes/UmsatzAuswertung/UmsatzAuswertung')),
    paths: ['/umsatz-auswertung'],
    right: 'orders.anzeigen',
  },
  WebVitalsAuswertung: {
    Component: lazy(()=>import('src/routes/WebVitalsAuswertung/WebVitalsAuswertung')),
    paths: ['/web-vitals'],
    right: 'orders.anzeigen',
  },
  ContentSeoAuswertung: {
    Component: lazy(()=>import('src/routes/ContentSeoAuswertung/ContentSeoAuswertung')),
    paths: ['/content-seo-auswertung'],
    right: 'menues.anzeigen',
  },


  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /* ++++++++++++++++++++++++ Kategorien +++++++++++++++++++++++++++ */
  Kategoriefilter: {
    Component: lazy(()=>import('src/routes/Kategoriefilter/Kategoriefilter')),
    paths: ['/kategoriefilter'],
    right: 'categories.filter',
  },
  Kategorie: {
    Component: lazy(()=>import('src/routes/Kategorie/Kategorie')),
    paths: ['/kategorie/','/kategorie/:id'],
    right: 'categories.anzeigen',
  },
  NeueKategorie: {
    Component: lazy(()=>import('src/routes/NeueKategorie/NeueKategorie')),
    paths: ['/neuekategorie'],
    right: 'categories.bearbeiten',
  },

  
  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /* ++++++++++++++++++++++++ Hersteller +++++++++++++++++++++++++++ */
  Herstellerfilter: {
    Component: lazy(()=>import('src/routes/Herstellerfilter/Herstellerfilter')),
    paths: ['/herstellerfilter'],
    right: 'brands.filter',
  },
  Hersteller: {
    Component: lazy(()=>import('src/routes/Hersteller/Hersteller')),
    paths: ['/hersteller/','/hersteller/:id'],
    right: 'brands.anzeigen',
  },
  NeuerHersteller: {
    Component: lazy(()=>import('src/routes/NeuerHersteller/NeuerHersteller')),
    paths: ['/neuerhersteller'],
    right: 'brands.bearbeiten',
  },


  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /* ++++++++++++++++++++++++ Bestellungen +++++++++++++++++++++++++ */
  Bestellungen: {
    Component: lazy(()=>import('src/routes/Bestellungen/Bestellungen')),
    paths: ['/bestellungen'],
    right: 'orders.anzeigen',
  },
  Bestellung: {
    Component: lazy(()=>import('src/routes/Bestellung/Bestellung')),
    paths: ['/bestellung/','/bestellung/:id'],
    right: 'orders.anzeigen',
  },

  
  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /* +++++++++++++++++++++++++ Filter ++++++++++++++++++++++++++++++ */
  Filterfilter: {
    Component: lazy(()=>import('src/routes/Filterfilter/Filterfilter')),
    paths: ['/filterfilter'],
    right: 'filter.filter',
  },
  Filter: {
    Component: lazy(()=>import('src/routes/Filter/Filter')),
    paths: ['/filter/','/filter/:id'],
    right: 'filter.anzeigen',
  },
  NeuerFilter: {
    Component: lazy(()=>import('src/routes/NeuerFilter/NeuerFilter')),
    paths: ['/neuerfilter'],
    right: 'filter.bearbeiten',
  },


  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /* +++++++++++++++++++++++++++ Menüs +++++++++++++++++++++++++++++ */
  MenuFilter: {
    Component: lazy(()=>import('src/routes/Menufilter/Menufilter')),
    paths: ['/menues'],
    right: 'menues.filter',
  },
  Menu: {
    Component: lazy(()=>import('src/routes/Menu/Menu')),
    paths: ['/menu/','/menu/:id'],
    right: 'menues.anzeigen',
  },
  NeuesMenu: {
    Component: lazy(()=>import('src/routes/NeuesMenu/NeuesMenu')),
    paths: ['/neuesmenu'],
    right: 'menues.bearbeiten',
  },


  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /* +++++++++++++++++++++++++ Menü-Seiten +++++++++++++++++++++++++ */
  MenueSeitenFilter: {
    Component: lazy(()=>import('src/routes/MenueSeitenfilter/MenueSeitenfilter')),
    paths: ['/menueseiten'],
    right: 'menues.filter',
  },
  MenueSeite: {
    Component: lazy(()=>import('src/routes/MenueSeite/MenueSeite')),
    paths: ['/menueseite/','/menueseite/:id'],
    right: 'menues.anzeigen',
  },
  NeueMenuSeite: {
    Component: lazy(()=>import('src/routes/NeueMenueSeite/NeueMenueSeite')),
    paths: ['/neuemenueseite'],
    right: 'menues.bearbeiten',
  },


  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /* +++++++++++++++++++++++++++ Magazine ++++++++++++++++++++++++++ */
  Magazinefilter: {
    Component: lazy(()=>import('src/routes/Magazinefilter/Magazinefilter')),
    paths: ['/magazine'],
    right: 'magazines.filter',
  },
  Magazin: {
    Component: lazy(()=>import('src/routes/Magazin/Magazin')),
    paths: ['/magazin/','/magazin/:id'],
    right: 'magazines.anzeigen',
  },
  NeuesMagazin: {
    Component: lazy(()=>import('src/routes/NeuesMagazin/NeuesMagazin')),
    paths: ['/neuesmagazin'],
    right: 'magazines.bearbeiten',
  },

  
  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /* ++++++++++++++++++++++ Preis Spider +++++++++++++++++++++++++++ */
  
  CrawlerFilter: {
    Component: lazy(()=>import('src/routes/CrawlerFilter/CrawlerFilter')),
    paths: ['/crawlers'],
    right: 'crawlers.filter',
    feature: 'priceSpy',
  },
  Crawler: {
    Component: lazy(()=>import('src/routes/Crawler/Crawler')),
    paths: ['/crawler/','/crawler/:id'],
    right: 'crawlers.anzeigen',
    feature: 'priceSpy',
  },
  NeuerCrawler: {
    Component: lazy(()=>import('src/routes/NeuerCrawler/NeuerCrawler')),
    paths: ['/neuercrawler'],
    right: 'crawlers.bearbeiten',
    feature: 'priceSpy',
  },

  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /* +++++++++++++++++++++++++ Coupons +++++++++++++++++++++++++++++ */
  
  CouponFilter: {
    Component: lazy(()=>import('src/routes/CouponFilter/CouponFilter')),
    paths: ['/coupons'],
    right: 'coupons.filter',
  },
  Coupon: {
    Component: lazy(()=>import('src/routes/Coupon/Coupon')),
    paths: ['/coupon/','/coupon/:id'],
    right: 'coupons.anzeigen',
  },
  NeuerCoupon: {
    Component: lazy(()=>import('src/routes/NeuerCoupon/NeuerCoupon')),
    paths: ['/neuercoupon'],
    right: 'coupons.bearbeiten',
  },
  
  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /* +++++++++++++++++++++++++ System ++++++++++++++++++++++++++++++ */
  Benutzer: {
    Component: lazy(()=>import('src/routes/Benutzer/Benutzer')),
    paths: ['/benutzer'],
    right: 'system.benutzer',
  },
  Benutzerverwaltung: {
    Component: lazy(()=>import('src/routes/Benutzerverwaltung/Benutzerverwaltung')),
    paths: ['/benutzerverwaltung'],
    right: 'system.benutzerverwaltung',
  },
  Server: {
    Component: lazy(()=>import('src/routes/Server/Server')),
    paths: ['/server'],
    right: 'system.server',
  },
  EbayKonten: {
    Component: lazy(()=>import('src/routes/EbayKonten/EbayKonten')),
    paths: ['/ebay-konten'],
    right: 'system.ebay',
  },
  MetaKataloge: {
    Component: lazy(()=>import('src/routes/MetaKataloge/MetaKataloge')),
    paths: ['/meta-kataloge'],
    right: 'system.meta',
  },
  Landingpages: {
    Component: lazy(()=>import('src/routes/Landingpages/Landingpages')),
    paths: ['/landingpages'],
    right: 'menues.bearbeiten',
  },


  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

}

export type tRouteNames = keyof typeof routes

export default routes as tRoutes
